import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { isDefined } from '../../../../../utils/variables';
import { AppContext, UserRole } from '../../../../state/app-state';
import { useCompanyState } from '../../../../state/company-state';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { ThemeManager } from '../../../../themes/theme-manager';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import {
  buildDroneHarmonyIntegration,
  getDonglesIntegration,
  getLeicaIntegration,
  getMobaXsiteIntegration,
  IntegrationItem,
  IntegrationsContext,
  IntegrationStatus,
} from '../../state/integrations-state';
import { AcceptSkyMapConnectLicenseAgreementDialog } from '../accept-skymap-connect-license-agreement-dialog/accept-skymap-connect-license-agreement-dialog';
import { DisabledState, IntegrationCard } from './integration-card';

interface Props {
  onClose: () => void;
}

const NewIntegrationDialog = (props: Props) => {
  const { t } = useTranslation('integrations');
  const { addIntegration } = React.useContext(IntegrationsContext);
  const { integrations, agreementAccepted, setAgreementAccepted } =
    React.useContext(IntegrationsContext);
  const { project } = React.useContext(ProjectContext);
  const { currentUser } = React.useContext(AppContext);
  const { getCompanyCountry } = useCompanyState();

  const [agreementDialogVisible, setAgreementDialogVisible] = React.useState(false);
  const [integrationItem, setIntegrationItem] = React.useState<IntegrationItem | undefined>(
    undefined,
  );

  const onSelect = (integrationItem: IntegrationItem) => {
    return () => {
      if (
        !agreementAccepted &&
        ThemeManager.instance.theme.themeName !== 'moba' &&
        integrationItem.requireConnectAgreement
      ) {
        setAgreementDialogVisible(true);
        setIntegrationItem(integrationItem);
      } else {
        addSelectedIntegration(integrationItem);
        props.onClose();
      }
    };
  };

  const addSelectedIntegration = (integrationItem: IntegrationItem) => {
    addIntegration(integrationItem);
  };

  const onAgreementDialogClosed = (accepted: boolean) => {
    if (accepted && isDefined(integrationItem)) {
      addSelectedIntegration(integrationItem);
      setAgreementAccepted(true);
      props.onClose();
    } else {
      setAgreementDialogVisible(false);
    }
  };

  const resolveIntegrationDisabled = (
    key: 'leica_conx' | 'xsite_manage' | 'dongles' | 'drone_harmony',
  ): DisabledState | undefined => {
    const userIsOrganizationAdmin = currentUser?.selectedRole === UserRole.OrganizationAdmin;
    const integrationExists = isDefined(integrations.find((x) => x.key === key));
    const integrationNotAvailableInCountry =
      key === 'leica_conx' && getCompanyCountry()?.alpha2Code !== 'SE';

    if (userIsOrganizationAdmin) {
      return {
        tooltip: t('connection.item.disabled.isOrganizationAdmin.description', {
          ns: 'integrations',
        }),
      };
    }
    if (integrationExists) {
      return {
        tooltip: t('connection.item.disabled.alreadyConnected.title', { ns: 'integrations' }),
      };
    }
    if (integrationNotAvailableInCountry) {
      return {
        tooltip: t('connection.item.disabled.isNotAvailableOutsideOfSweden.description', {
          ns: 'integrations',
        }),
      };
    }
  };

  return !agreementDialogVisible ? (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      useGoogleTranslate={true}
      onClose={props.onClose}
    >
      {{
        header: t('integrationList.dialog.title', { ns: 'integrations' }),
        content: (
          <Content>
            <InfoBox color="yellow" padding="medium">
              {t('integrationList.dialog.helpText', { ns: 'integrations' })}
            </InfoBox>

            <Cards>
              {ThemeManager.instance.theme.themeName !== 'moba' && (
                <IntegrationCard
                  description={
                    <>
                      {t('integrationList.cards.leica.description', { ns: 'integrations' })}{' '}
                      <span className="notranslate">Leica ConX</span>.
                    </>
                  }
                  disabled={resolveIntegrationDisabled('leica_conx')}
                  logo="/static/integrations/leica/logotype.png"
                  title={t('integrationList.cards.leica.title', { ns: 'integrations' })}
                  onSelect={onSelect(getLeicaIntegration(IntegrationStatus.Setup))}
                />
              )}

              <IntegrationCard
                description={
                  <>
                    {t('integrationList.cards.xsite.description', { ns: 'integrations' })}{' '}
                    <span className="notranslate">Xsite Manage</span>.
                  </>
                }
                disabled={resolveIntegrationDisabled('xsite_manage')}
                logo="/static/integrations/xsite/novatron.png"
                title={t('integrationList.cards.xsite.title', { ns: 'integrations' })}
                onSelect={onSelect(getMobaXsiteIntegration(IntegrationStatus.Setup))}
              />
              {ThemeManager.instance.theme.themeName !== 'moba' && (
                <IntegrationCard
                  description={
                    <>
                      {t('integrationList.cards.droneHarmony.description', { ns: 'integrations' })}
                    </>
                  }
                  disabled={resolveIntegrationDisabled('drone_harmony')}
                  logo="/static/integrations/drone-harmony/logotype.png"
                  title={t('integrationList.cards.droneHarmony.title', { ns: 'integrations' })}
                  onSelect={onSelect(
                    buildDroneHarmonyIntegration(project.id, {
                      status: IntegrationStatus.Setup,
                    }),
                  )}
                />
              )}
              {FeatureFlagsManager.instance.isEnabled('SK-3938-skymap-dongles') && (
                <IntegrationCard
                  description={
                    <>
                      {t('integrationList.cards.dongles.description', { ns: 'integrations' })}{' '}
                      <span className="notranslate">SkyMap Portal</span> och{' '}
                      <span className="notranslate">SkyMap Dongles</span>.
                    </>
                  }
                  disabled={resolveIntegrationDisabled('dongles')}
                  logo="/static/themes/default/images/logotype.png"
                  title={t('integrationList.cards.dongles.title', { ns: 'integrations' })}
                  useBorderRadius={false}
                  onSelect={onSelect(getDonglesIntegration(IntegrationStatus.Setup, project.id))}
                />
              )}
            </Cards>
          </Content>
        ),
        footer: {
          right: (
            <Button variant="text" onClick={props.onClose}>
              {t('integrationList.dialog.close', { ns: 'integrations' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  ) : (
    <AcceptSkyMapConnectLicenseAgreementDialog onClose={onAgreementDialogClosed} />
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  align-items: center;
`;

const Cards = styled.div`
  display: flex;
  gap: 1em;
  align-items: stretch;
`;

export { NewIntegrationDialog };
